import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Globe, MapPin, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import ResourcePage from "@/components/common/ResourcePage";
import { EntityFormDialog } from "@/components/common/EntityFormDialog";
import { ErrorState, LoadingState, Pill } from "@/components/common/States";
import { OpportunityPanel } from "@/components/crm/OpportunityPanel";
import { Timeline } from "@/components/crm/Timeline";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useFetch, useLookups } from "@/lib/hooks";
import { COMPANY_STATUS, companies, contacts, labelOf, opportunities, sites, tasks } from "@/lib/resources";
import { errorMessage, fmtMoney } from "@/lib/api";
import { evidence, projects } from "@/lib/researchResources";
import { useResearchActions } from "@/pages/Research";

function DuplicateWarning({ company }) {
  const { data } = useFetch("/dedupe/companies", { name: company.name, domain: company.domain || "", exclude_id: company.id });
  if (!data?.length) return null;
  return (
    <div className="mb-6 flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm" data-testid="duplicate-warning">
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
      <div>Possible duplicate{data.length > 1 ? "s" : ""}:{" "}
        {data.map((d, i) => <span key={d.id}>{i > 0 && ", "}<Link className="text-primary hover:underline" to={`/companies/${d.id}`}>{d.name}</Link> <span className="text-muted-foreground">({d.reasons.join(", ")})</span></span>)}
      </div>
    </div>
  );
}

const Fact = ({ label, value }) => <div><p className="eyebrow mb-1">{label}</p><p className="text-sm">{value || <span className="text-muted-foreground">—</span>}</p></div>;

function Overview({ c, members }) {
  return (
    <div className="panel grid gap-5 p-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="company-overview">
      <Fact label="Legal name" value={c.legal_name} /><Fact label="Registration no." value={c.registration_number} />
      <Fact label="Industry" value={c.industry} /><Fact label="Employees" value={c.employee_count} />
      <Fact label="Annual revenue" value={c.annual_revenue != null && fmtMoney(c.annual_revenue)} /><Fact label="Registered address" value={c.registered_address} />
      <Fact label="Owner" value={members?.[c.owner_user_id]} /><Fact label="Source" value={c.source} />
      <div className="sm:col-span-2 lg:col-span-4"><Fact label="Description" value={c.description} /></div>
    </div>
  );
}

function DeleteCompany({ company }) {
  const { api } = useWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const remove = async () => {
    try {
      const { data: r } = await api.post("/companies/bulk-delete", { ids: [company.id] });
      if (r.deleted) { toast.success("Company deleted"); navigate("/companies"); }
      else toast.error(`Not deleted: ${r.skipped[0]?.reason || "not found"}. Delete or move those records first.`, { duration: 9000 });
    } catch (e) { toast.error(errorMessage(e)); }
    setOpen(false);
  };
  return (
    <>
      <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => setOpen(true)} data-testid="company-delete-button"><Trash2 className="mr-1.5 h-4 w-4" />Delete</Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="company-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {company.name}?</AlertDialogTitle>
            <AlertDialogDescription>It is removed from your CRM and will not be re-added by future research runs. A company still linked to contacts, sites, opportunities or tasks cannot be deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="company-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="company-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function CompanyDetail() {
  const { id } = useParams();
  const { api, can } = useWorkspace();
  const { data: c, loading, error, reload } = useFetch(`/companies/${id}`);
  const lookups = useLookups(["members"]);
  const [editing, setEditing] = useState(false);
  const [opp, setOpp] = useState(null);
  const [oppKey, setOppKey] = useState(0);
  const { evidenceActions } = useResearchActions();
  if (loading && !c) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} testid="company-error" />;
  const fixed = { company_id: id };
  const save = async (payload) => { await api.patch(`/companies/${id}`, payload); toast.success("Company updated"); reload(); };

  return (
    <div data-testid="company-detail-page">
      <Link to="/companies" className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-companies"><ArrowLeft className="h-3.5 w-3.5" />Companies</Link>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between fade-up">
        <div>
          <div className="mb-2 flex gap-2"><Pill value={c.status}>{labelOf(COMPANY_STATUS, c.status)}</Pill>{(c.tags || []).map((t) => <Pill key={t} tone="slate">{t}</Pill>)}</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="company-name">{c.name}</h1>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted-foreground">
            {(c.city || c.country) && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[c.city, c.postcode, c.country].filter(Boolean).join(", ")}</span>}
            {c.domain && <span className="inline-flex items-center gap-1 font-mono text-xs"><Globe className="h-3.5 w-3.5" />{c.domain}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {can("write") && <Button variant="outline" onClick={() => setEditing(true)} data-testid="company-edit-button"><Pencil className="mr-1.5 h-4 w-4" />Edit company</Button>}
          {can("delete") && <DeleteCompany company={c} />}
        </div>
      </div>
      <DuplicateWarning company={c} />
      <Tabs defaultValue="overview">
        <TabsList className="mb-4 flex h-auto flex-wrap justify-start">
          {["overview", "sites", "contacts", "opportunities", "projects", "evidence", "tasks", "timeline"].map((t) => <TabsTrigger key={t} value={t} className="capitalize" data-testid={`company-tab-${t}`}>{t === "sites" ? "Operating sites" : t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview"><Overview c={c} members={lookups.members} /></TabsContent>
        <TabsContent value="sites"><ResourcePage config={sites} fixedParams={fixed} createDefaults={fixed} /></TabsContent>
        <TabsContent value="contacts"><ResourcePage config={contacts} fixedParams={fixed} createDefaults={fixed} /></TabsContent>
        <TabsContent value="opportunities"><ResourcePage config={opportunities} fixedParams={fixed} createDefaults={fixed} onRowClick={(r) => setOpp(r.id)} reloadKey={oppKey} /></TabsContent>
        <TabsContent value="projects"><ResourcePage config={projects} fixedParams={fixed} createDefaults={fixed} /></TabsContent>
        <TabsContent value="evidence"><ResourcePage config={evidence} fixedParams={fixed} createDefaults={fixed} extraRowActions={evidenceActions} /></TabsContent>
        <TabsContent value="tasks"><ResourcePage config={tasks} fixedParams={fixed} createDefaults={fixed} /></TabsContent>
        <TabsContent value="timeline"><div className="panel p-6"><Timeline activityParams={fixed} entityId={id} link={fixed} testid="company-timeline" /></div></TabsContent>
      </Tabs>
      <EntityFormDialog open={editing} onOpenChange={setEditing} title="Edit company" fields={companies.fields} record={c} lookups={lookups} onSubmit={save} testid="company-form" />
      <OpportunityPanel id={opp} onClose={() => setOpp(null)} onChanged={() => setOppKey((k) => k + 1)} />
    </div>
  );
}
